import { Component, Inject, OnInit } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { TranslateService } from '@ngx-translate/core';
import { DayOfWeek } from 'app/models/organization.model';
import { MenuScheduleChangeRequestItem, MenuScheduleModalData } from 'app/models/menu.schedule.model';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MenuScheduleApiService } from 'app/services/menu-schedule.api.service';
import {
  SelectProductsScheduleModalComponent
} from 'app/pages/menu/shared/select-products-schedule-modal/select-products-schedule-modal.component';
import {
  SelectGroupsScheduleModalComponent
} from 'app/pages/menu/shared/select-groups-schedule-modal/select-groups-schedule-modal.component';

@Component({
  selector: 'app-menu-schedules-modal',
  templateUrl: './menu-schedules-modal.component.html',
  styleUrls: ['./menu-schedules-modal.component.scss']
})
export class MenuSchedulesModalComponent implements OnInit {
  menuSectionId: string;
  dataSource = new MatTableDataSource<MenuScheduleModalData>([]);
  dataIsUploaded: boolean = false;

  constructor(
    private dialogRef: MatDialogRef<MenuSchedulesModalComponent>,
    private translate: TranslateService,
    private menuScheduleApiService: MenuScheduleApiService,
    @Inject(MAT_DIALOG_DATA) public data: string, // menuSectionId
    private dialog: MatDialog,
  ) {
    this.menuSectionId = this.data;
  }

  async ngOnInit() {
    let responseSchedules = await this.menuScheduleApiService.getSchedules(this.menuSectionId);
    let schedules = (responseSchedules.data as MenuScheduleModalData[]).map(s => {
      s.key = s.id;
      s.initialValue = {...s};
      if (s.externalProductIds?.length > 0) {
        s.initialValue.externalProductIds = [...s.externalProductIds];
      }
      if (s.groupIds?.length > 0) {
        s.initialValue.groupIds = [...s.groupIds];
      }
      return s;
    });
    this.dataSource = new MatTableDataSource<MenuScheduleModalData>(schedules);
    this.dataIsUploaded = true;
  }

  columnsToDisplay = ['dayOfWeek', 'from', 'to', 'allMenu', 'name', 'edit', 'isActive', 'delete'];
  getColumnHeaderName(columnHeaderValue: string): string {
    switch (columnHeaderValue) {
      case 'dayOfWeek':
        return this.translate.instant('MENU.DAY_OF_WEEK');
      case 'from':
        return this.translate.instant('MENU.FROM');
      case 'to':
        return this.translate.instant('MENU.TO');
      case 'allMenu':
        return this.translate.instant('COMMON.ALL');
      case 'name':
        return this.translate.instant('COMMON.NAME');
      case 'edit':
        return this.translate.instant('COMMON.EDIT');
      case 'isActive':
        return this.translate.instant('COMMON.IS_ACTIVE');
      case 'delete':
        return this.translate.instant('COMMON.DELETE');
    }
  }

  changeAllMenu(scheduleKey: string, checked: boolean) {
    this.dataSource.data.find(s => s.key == scheduleKey).allMenu = checked;
  }

  changeIsActive(scheduleKey: string, checked: boolean) {
    this.dataSource.data.find(s => s.key == scheduleKey).isActive = checked;
  }

  onAddClick() {
    this.dataSource.data.unshift({
      id: null,
      key: Date.now().toString(), // необходимо уникальное значение, чтобы было видно, какое расписание в данный момент редактируется.
      dayOfWeek: DayOfWeek.EveryDay,
      from: '08:00',
      to: '23:00',
      isActive: true,
      allMenu: true,
      name: '',
      isCreated: true,
      isDeleted: false,
      initialValue: null,
      isUpdated: false,
      isUpdatedProducts: false,
      isUpdatedGroups: false,
      externalProductIds: [],
      groupIds: [],
      menuSectionId: this.menuSectionId,
    });
    this.dataSource._updateChangeSubscription();
  }

  dayOfWeekSelectionChange(scheduleKey: string, value: DayOfWeek) {
    this.dataSource.data.find(s => s.key == scheduleKey).dayOfWeek = value;
  }

  onFromChange(scheduleKey: string, value: string) {
    this.dataSource.data.find(s => s.key == scheduleKey).from = value;
  }

  onToChange(scheduleKey: string, value: string) {
    this.dataSource.data.find(s => s.key == scheduleKey).to = value;
  }

  onNameChange(scheduleKey: string, value: string) {
    this.dataSource.data.find(s => s.key == scheduleKey).name = value;
  }

  selectProductsClick(scheduleKey: string){
    this.dialog.open<SelectProductsScheduleModalComponent, MenuScheduleModalData>(
      SelectProductsScheduleModalComponent,
      {
        data: this.dataSource.data.find(s => s.key == scheduleKey),
      });
  }

  selectGroupsClick(scheduleKey: string){
    this.dialog.open<SelectGroupsScheduleModalComponent, MenuScheduleModalData>(
      SelectGroupsScheduleModalComponent,
      {
        data: this.dataSource.data.find(s => s.key == scheduleKey),
      });
  }

  clearScheduleClick(scheduleKey: string) {
    this.dataSource.data.find(s => s.key == scheduleKey).isDeleted = true;
  }

  async saveAllAsync() {
    const requestItems: MenuScheduleChangeRequestItem[] = [];
    for (let schedule of this.dataSource.data) {
      const scheduleChangeRequestItem = this.convertSchedule(schedule);
      if (schedule.isCreated || schedule.isDeleted){
        requestItems.push(scheduleChangeRequestItem);
        continue;
      }

      let isUpdated = false;

      if (this.isChangedSchedule(schedule)){
        scheduleChangeRequestItem.isUpdated = true;
        isUpdated = true;
      }

      if (this.isChangedProductIds(schedule)){
        scheduleChangeRequestItem.isUpdatedProducts = true;
        isUpdated = true;
      }

      if (this.isChangedGroupIds(schedule)){
        scheduleChangeRequestItem.isUpdatedGroups = true;
        isUpdated = true;
      }

      if (isUpdated){
        requestItems.push(scheduleChangeRequestItem);
      }
    }

    if (requestItems.length < 1) {
      return;
    }

    let responseItems = await this.menuScheduleApiService.changeSchedules(requestItems);
    if (responseItems.success){
      this.dialogRef.close();
    }
    else{
      alert("error");
    }
  }

  exit() {
    this.dialogRef.close();
  }

  isChanged(schedule: MenuScheduleModalData){
    return !schedule.isCreated && !schedule.isDeleted &&
      (this.isChangedSchedule(schedule)
      || this.isChangedProductIds(schedule)
      || this.isChangedGroupIds(schedule));
  }

  isChangedSchedule(schedule: MenuScheduleModalData){
    return schedule.dayOfWeek !== schedule.initialValue.dayOfWeek
      || schedule.name !== schedule.initialValue.name
      || schedule.allMenu !== schedule.initialValue.allMenu
      || schedule.isActive !== schedule.initialValue.isActive
      || schedule.from !== schedule.initialValue.from
      || schedule.to !== schedule.initialValue.to;
  }

  isChangedProductIds(schedule: MenuScheduleModalData){
    return JSON.stringify(schedule.externalProductIds??[]) !== JSON.stringify(schedule.initialValue?.externalProductIds??[]);
  }

  isChangedGroupIds(schedule: MenuScheduleModalData){
    return JSON.stringify(schedule.groupIds??[]) !== JSON.stringify(schedule.initialValue?.groupIds??[]);
  }

  cancelChange(schedule: MenuScheduleModalData){
    if (schedule.isCreated){
      const index = this.dataSource.data.indexOf(schedule);
      this.dataSource.data.splice(index, 1);
      this.dataSource._updateChangeSubscription();
      return;
    }
    if(schedule.isDeleted){
      schedule.isDeleted = false;
    }
    if (this.isChanged(schedule)){
      const currentValue = schedule.initialValue;
      Object.assign(schedule, schedule.initialValue);
      // т.к. Object.assign игнорирует undefined поля
      if (schedule.initialValue.externalProductIds === undefined){
        schedule.externalProductIds = [];
      }
      if (schedule.initialValue.groupIds === undefined){
        schedule.groupIds = [];
      }
      schedule.initialValue = currentValue;
    }
  }

  convertSchedule(schedule: MenuScheduleModalData): MenuScheduleChangeRequestItem {
    return {
      menuSectionId: schedule.menuSectionId,
      id: schedule.id,
      name: schedule.name,
      dayOfWeek: schedule.dayOfWeek,
      from: schedule.from,
      to: schedule.to,
      allMenu: schedule.allMenu,
      isActive: schedule.isActive,
      isCreated: schedule.isCreated,
      isUpdated: schedule.isUpdated,
      isDeleted: schedule.isDeleted,
      isUpdatedProducts: schedule.isUpdatedProducts,
      isUpdatedGroups: schedule.isUpdatedGroups,
      externalProductIds: schedule.externalProductIds,
      groupIds: schedule.groupIds,
    };
  }
}
